import { DragEvent, memo, useState } from "react"
import { enqueueSnackbar } from "notistack"
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faGripDotsVertical, faLeaf, faList, faWavePulse } from "@fortawesome/pro-light-svg-icons"
import { faRotateLeft } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { DishParameterType } from "../../../templates/menuTemplatesTypes"

import { RecentDish, useWeekMenu } from "../../WeekMenuContext"
import { cellForWeek } from "../../useWeekMenuStore"
import {
    cellStateFor,
    isFieldLocked,
    isMandatoryAllergensMissing,
    isMandatoryFieldMissing,
    isMandatoryPhotoMissing,
    rowHasTemplateDefaults,
} from "../../weekMenuLib"
import { WeekDishRow, WeekMenuCell, WeekMenuCellState, WeekMenuCellValues } from "../../weekMenuTypes"
import { CellAllergensField } from "./CellAllergensField"
import { CellStatusTag } from "./CellStatusTag"
import { CellDescriptionField } from "./CellDescriptionField"
import { CellImageField } from "./CellImageField"
import { CellDetailsDialog } from "./CellDetailsDialog"
import { CellTools } from "./CellTools"
import { CellMarketingField } from "./CellMarketingField"
import { CellPriceField } from "./CellPriceField"
import { CellTitleField } from "./CellTitleField"
import { CopyDishDialog } from "./CopyDishDialog"

const useStyles = makeStyles<void, "dragHandle">()((theme, _, classes) => ({
    cell: {
        position: "relative",
        height: "100%",
        minHeight: "9rem",
        padding: ".625rem .625rem .75rem",
        borderLeft: `.25rem solid ${theme.palette.tailwind.slate[300]}`,
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        background: theme.palette.background.paper,
        // Only the drag handle is hover revealed; the ⋮ menu stays always available.
        [`&:hover .${classes.dragHandle}`]: {
            opacity: 1,
        },
    },
    cellFilled: {
        borderLeftColor: theme.palette.tailwind.green[600],
    },
    cellNeedsFill: {
        borderLeftColor: theme.palette.tailwind.amber[500],
        background: theme.palette.tailwind.amber[50],
    },
    cellDefault: {
        borderLeftColor: theme.palette.tailwind.slate[300],
    },
    cellUnavailable: {
        background: `repeating-linear-gradient(45deg, ${theme.palette.tailwind.gray[50]}, ${theme.palette.tailwind.gray[50]} .375rem, ${theme.palette.tailwind.gray[100]} .375rem, ${theme.palette.tailwind.gray[100]} .75rem)`,
    },
    cellAiFresh: {
        boxShadow: `inset 0 0 0 1px ${theme.palette.tailwind.violet[200]}`,
        background: theme.palette.tailwind.violet[50],
    },
    cellDragging: {
        opacity: 0.35,
    },
    cellDropEligible: {
        outline: `1px dashed ${theme.palette.tailwind.blue[300]}`,
        outlineOffset: "-.125rem",
    },
    cellDropTarget: {
        outline: `.125rem dashed ${theme.palette.tailwind.blue[600]}`,
        outlineOffset: "-.25rem",
        background: theme.palette.tailwind.blue[50],
    },
    unavailableText: {
        color: theme.palette.tailwind.gray[400],
        fontStyle: "italic",
    },
    lockedValue: {
        padding: ".25rem .375rem",
        borderRadius: ".25rem",
        background: theme.palette.tailwind.indigo[50],
        color: theme.palette.tailwind.indigo[800],
    },
    tools: {
        position: "absolute",
        top: ".375rem",
        right: ".375rem",
        zIndex: 2,
    },
    dragHandle: {
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: ".25rem",
        color: theme.palette.tailwind.gray[400],
        cursor: "grab",
        opacity: 0,
        transition: theme.transitions.create("opacity", { duration: theme.transitions.duration.shortest }),
        "& svg": {
            fontSize: ".875rem",
        },
        "&:active": {
            cursor: "grabbing",
        },
    },
    aiRegenButton: {
        position: "absolute",
        right: ".5rem",
        bottom: ".5rem",
        zIndex: 3,
        width: "1.625rem",
        height: "1.625rem",
        borderRadius: ".5rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        background: theme.palette.background.paper,
        color: theme.palette.tailwind.slate[800],
        boxShadow: theme.shadows[1],
        "& svg": {
            fontSize: ".875rem",
        },
        "&:hover": {
            background: theme.palette.tailwind.slate[50],
            borderColor: theme.palette.tailwind.slate[400],
        },
    },
    // Reserves the corner tools' width so the title input and its focus outline stay clear of them.
    titleArea: {
        paddingRight: "3.75rem",
    },
    priceImageRow: {
        width: "100%",
    },
    detailIndicators: {
        marginTop: ".5rem",
        color: theme.palette.tailwind.gray[600],
        "& svg": {
            fontSize: ".875rem",
        },
    },
    detailIndicator: {
        cursor: "help",
    },
}))

type Props = Readonly<{
    row: WeekDishRow
    dayIndex: number
    cell: WeekMenuCell
    /** Freshly AI generated, highlighted while the review banner is open. */
    isAiFresh: boolean
}>

// Memoized: only the edited cell re-renders on a keystroke; the rest keep their `cell` identity.
export const DishDayCell = memo(function DishDayCell({ row, dayIndex, cell, isAiFresh }: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const {
        actions,
        isAiReviewActive,
        allergenOptions,
        getAiDaySettings,
        getWeek,
        dayLabels,
        dragSource,
        onCellDragStart,
        onCellDragEnd,
    } = useWeekMenu()

    const [isCopyDialogOpen, setCopyDialogOpen] = useState(false)
    const [isDetailsOpen, setDetailsOpen] = useState(false)
    const [isDropTarget, setDropTarget] = useState(false)
    const [isDragEnabled, setDragEnabled] = useState(false)

    const isAvailable = row.availableDayIndexes.includes(dayIndex)

    if (!isAvailable) {
        return (
            <Stack alignItems="center" justifyContent="center" className={cx(classes.cell, classes.cellUnavailable)}>
                <LocalizedStrict id="merchant-week-menu-cell-unavailable">
                    <Typography variant="caption" className={classes.unavailableText}>
                        Not available
                    </Typography>
                </LocalizedStrict>
            </Stack>
        )
    }

    const state = cellStateFor(row, cell.values, cell.isTouched)
    const showMissing = state === WeekMenuCellState.NEEDS_FILL
    const hasContent = state === WeekMenuCellState.FILLED || state === WeekMenuCellState.NEEDS_FILL

    const stateClasses: Record<WeekMenuCellState, string | undefined> = {
        [WeekMenuCellState.FILLED]: classes.cellFilled,
        [WeekMenuCellState.NEEDS_FILL]: classes.cellNeedsFill,
        [WeekMenuCellState.TEMPLATE_DEFAULT]: classes.cellDefault,
        [WeekMenuCellState.EMPTY]: undefined,
    }

    const isSourceCell = dragSource !== null && dragSource.rowKey === row.key && dragSource.dayIndex === dayIndex
    const isDropEligible = dragSource !== null && dragSource.rowKey === row.key && !isSourceCell

    const handleDragStart = (event: DragEvent) => {
        onCellDragStart({ rowKey: row.key, dayIndex })
        event.dataTransfer.effectAllowed = "move"
        // Without explicit data some browsers attach the page URL, which a drop would paste as text.
        event.dataTransfer.setData("text/plain", "")
    }

    const handleDragOver = (event: DragEvent) => {
        if (!isDropEligible) return
        event.preventDefault()
        setDropTarget(true)
    }

    const handleDrop = (event: DragEvent) => {
        event.preventDefault()
        setDropTarget(false)
        if (!isDropEligible || dragSource === null) return
        const draggedTitle = cellForWeek(getWeek(), row, dragSource.dayIndex).values.title
        actions.swapCells(row, dragSource.dayIndex, dayIndex)
        enqueueSnackbar({
            variant: "success",
            message: l10n.getString("merchant-week-menu-swap-snackbar", {
                dish: draggedTitle.trim() || row.name,
                dayA: dayLabels[dragSource.dayIndex],
                dayB: dayLabels[dayIndex],
            }),
        })
        onCellDragEnd()
    }

    const handlePickDish = (dish: RecentDish) => {
        const values: Partial<WeekMenuCellValues> = {
            title: dish.title,
            ...(row.description.isShown && !isFieldLocked(row.description) && { description: dish.description }),
            ...(row.price.isShown && !isFieldLocked(row.price) && { price: dish.price }),
            ...(row.allergens.isShown && !isFieldLocked(row.allergens) && { allergens: dish.allergens }),
        }
        actions.updateCell(row, dayIndex, values)
    }

    const handleAiRegenerate = () => {
        actions.aiRegenerateCell(row, dayIndex, getAiDaySettings(dayIndex), allergenOptions)
        enqueueSnackbar({
            variant: "success",
            message: l10n.getString("merchant-week-menu-ai-regenerated-snackbar"),
        })
    }

    const descriptionDialogName = `${row.name}, ${dayLabels[dayIndex]}`

    // The locked all-week price lives in the row label, while pictures show in every cell.
    const showPriceInCell = row.price.isShown && !(isFieldLocked(row.price) && row.priceSameAllWeek)

    return (
        <Box
            className={cx(
                classes.cell,
                stateClasses[state],
                isAiReviewActive && isAiFresh && classes.cellAiFresh,
                isSourceCell && classes.cellDragging,
                isDropEligible && !isDropTarget && classes.cellDropEligible,
                isDropTarget && classes.cellDropTarget,
            )}
            draggable={isDragEnabled}
            onDragStart={handleDragStart}
            onDragEnd={() => {
                onCellDragEnd()
                setDragEnabled(false)
            }}
            onDragOver={handleDragOver}
            onDragLeave={() => setDropTarget(false)}
            onDrop={handleDrop}
        >
            <Stack direction="row" alignItems="flex-start" spacing=".25rem" className={classes.tools}>
                <LocalizedStrict id="merchant-week-menu-cell-drag-tooltip" attrs={{ title: true }}>
                    <Tooltip arrow title="Drag to another day">
                        <IconButton
                            size="small"
                            className={classes.dragHandle}
                            onMouseDown={() => setDragEnabled(true)}
                            onMouseUp={() => setDragEnabled(false)}
                        >
                            <FontAwesomeIcon icon={faGripDotsVertical} />
                        </IconButton>
                    </Tooltip>
                </LocalizedStrict>
                <CellTools
                    hasTemplateDefaults={rowHasTemplateDefaults(row)}
                    hasContent={hasContent}
                    onDetails={() => setDetailsOpen(true)}
                    onCopyToDays={() => setCopyDialogOpen(true)}
                    onClear={() => actions.clearCell(row, dayIndex)}
                    onResetToTemplate={() => {
                        actions.resetCellToTemplate(row, dayIndex)
                        enqueueSnackbar({
                            variant: "info",
                            message: l10n.getString("merchant-week-menu-reset-cell-snackbar"),
                        })
                    }}
                />
            </Stack>

            {isAiReviewActive && isAiFresh && (
                <LocalizedStrict id="merchant-week-menu-cell-ai-regen-tooltip" attrs={{ title: true }}>
                    <Tooltip arrow title="Regenerate with AI">
                        <IconButton size="small" className={classes.aiRegenButton} onClick={handleAiRegenerate}>
                            <FontAwesomeIcon icon={faRotateLeft} />
                        </IconButton>
                    </Tooltip>
                </LocalizedStrict>
            )}

            <Stack spacing=".375rem">
                <Box className={classes.titleArea}>
                    <CellTitleField
                        value={cell.values.title}
                        hasError={showMissing && cell.values.title.trim() === ""}
                        onChange={(title) => actions.updateCell(row, dayIndex, { title })}
                        onPickDish={handlePickDish}
                    />
                </Box>

                {row.description.isShown &&
                    (isFieldLocked(row.description) ? (
                        <Typography variant="caption" className={classes.lockedValue}>
                            {row.description.defaultValue}
                        </Typography>
                    ) : (
                        <CellDescriptionField
                            dishName={descriptionDialogName}
                            value={cell.values.description}
                            hasError={showMissing && isMandatoryFieldMissing(row.description, cell.values.description)}
                            isOptional={row.description.type === DishParameterType.OPTIONAL}
                            onChange={(description) => actions.updateCell(row, dayIndex, { description })}
                        />
                    ))}

                {row.allergens.isShown && (
                    <CellAllergensField
                        value={cell.values.allergens}
                        hasError={showMissing && isMandatoryAllergensMissing(row.allergens, cell.values.allergens)}
                        isReadOnly={isFieldLocked(row.allergens)}
                        isOptional={row.allergens.type === DishParameterType.OPTIONAL}
                        onChange={(allergens) => actions.updateCell(row, dayIndex, { allergens })}
                    />
                )}

                {row.marketingTag.isShown &&
                    (isFieldLocked(row.marketingTag) ? (
                        <Typography variant="caption" className={classes.lockedValue}>
                            {row.marketingTag.defaultValue}
                        </Typography>
                    ) : (
                        <CellMarketingField
                            value={cell.values.marketingTag}
                            hasError={
                                showMissing && isMandatoryFieldMissing(row.marketingTag, cell.values.marketingTag)
                            }
                            onChange={(marketingTag) => actions.updateCell(row, dayIndex, { marketingTag })}
                        />
                    ))}

                {(showPriceInCell || row.image.isShown) && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent={showPriceInCell ? "space-between" : "flex-end"}
                        spacing=".5rem"
                        className={classes.priceImageRow}
                    >
                        {showPriceInCell && (
                            <CellPriceField
                                value={cell.values.price}
                                hasError={showMissing && isMandatoryFieldMissing(row.price, cell.values.price)}
                                isLocked={isFieldLocked(row.price)}
                                isSameAllWeek={row.priceSameAllWeek}
                                onChange={(price) => actions.updateCell(row, dayIndex, { price })}
                            />
                        )}

                        {row.image.isShown && (
                            <CellImageField
                                photo={cell.values.photo}
                                hasError={showMissing && isMandatoryPhotoMissing(row.image, cell.values.photo)}
                                isLocked={isFieldLocked(row.image)}
                                onChange={(photo) => actions.updateCell(row, dayIndex, { photo })}
                            />
                        )}
                    </Stack>
                )}
            </Stack>

            {(cell.values.details.ingredients || cell.values.details.nutrition || cell.values.details.co2) && (
                <Stack direction="row" spacing=".5rem" className={classes.detailIndicators}>
                    {cell.values.details.ingredients && (
                        <Tooltip arrow title={cell.values.details.ingredients}>
                            <Stack className={classes.detailIndicator}>
                                <FontAwesomeIcon icon={faList} />
                            </Stack>
                        </Tooltip>
                    )}
                    {cell.values.details.nutrition && (
                        <Tooltip arrow title={cell.values.details.nutrition}>
                            <Stack className={classes.detailIndicator}>
                                <FontAwesomeIcon icon={faWavePulse} />
                            </Stack>
                        </Tooltip>
                    )}
                    {cell.values.details.co2 && (
                        <Tooltip
                            arrow
                            title={`${cell.values.details.co2} ${l10n.getString("merchant-week-menu-details-co2-unit")}`}
                        >
                            <Stack className={classes.detailIndicator}>
                                <FontAwesomeIcon icon={faLeaf} />
                            </Stack>
                        </Tooltip>
                    )}
                </Stack>
            )}

            <CellStatusTag row={row} values={cell.values} state={state} />

            {isCopyDialogOpen && (
                <CopyDishDialog
                    open={isCopyDialogOpen}
                    row={row}
                    sourceDay={dayIndex}
                    sourceTitle={cell.values.title}
                    dayLabels={dayLabels}
                    onClose={() => setCopyDialogOpen(false)}
                />
            )}
            {isDetailsOpen && (
                <CellDetailsDialog
                    open={isDetailsOpen}
                    row={row}
                    dishName={descriptionDialogName}
                    initialValues={cell.values}
                    onSave={(values) => {
                        actions.updateCell(row, dayIndex, values)
                        setDetailsOpen(false)
                    }}
                    onClose={() => setDetailsOpen(false)}
                />
            )}
        </Box>
    )
})
